import { useState } from "react";
import { useUserToken, setUserToken, clearUserToken } from "../lib/userToken";
import { useT } from "../i18n";

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard blocked — the Show view still lets them select it */
  }
}

// Top-right account menu. The per-user edit token is the only credential (the
// app is otherwise anonymous), so "account" = manage that token: copy it to move
// devices, reveal it, or forget it. With no token, offer paste-to-restore.
export function AccountMenu() {
  const t = useT();
  const token = useUserToken();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-mono text-xs border transition ${
          token ? "border-cyan/50 text-cyan" : "border-border text-dim hover:border-accent"
        }`}
      >
        {/* Phones: icon-only — the full label ("✓ Verifiziert") starved the
            nation chip out of the packed nav row. */}
        {token ? (
          <>
            ✓<span className="hidden sm:inline"> {t("account.verified")}</span>
          </>
        ) : (
          <>
            <span className="sm:hidden">👤</span>
            <span className="hidden sm:inline">{t("account.account")}</span>
          </>
        )}{" "}
        ▾
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-1.5rem)] z-50 card-x p-3">
            {token ? <ManageToken token={token} onClose={() => setOpen(false)} /> : <RestoreToken onClose={() => setOpen(false)} />}
          </div>
        </>
      )}
    </div>
  );
}

function ManageToken({ token, onClose }: { token: string; onClose: () => void }) {
  const t = useT();
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-2">
      <div className="eyebrow normal-case text-cyan">✓ {t("account.verifiedCandidate")}</div>
      <p className="text-sm text-muted">{t("account.tokenBlurb")}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { copy(token); setCopied(true); }}
          className="btn flex-1 text-center"
        >
          {copied ? `${t("account.copied")} ✓` : t("account.copyToken")}
        </button>
        <button type="button" onClick={() => setReveal((v) => !v)} className="btn-ghost btn">
          {reveal ? t("common.hide") : t("common.show")}
        </button>
      </div>
      {reveal && <code className="block bg-bg2 border border-border rounded px-2 py-1.5 text-[11px] break-all font-mono">{token}</code>}
      <button
        type="button"
        onClick={() => { clearUserToken(); onClose(); }}
        className="text-warn font-mono text-xs hover:opacity-80"
      >
        {t("account.forgetToken")}
      </button>
    </div>
  );
}

function RestoreToken({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [value, setValue] = useState("");
  return (
    <div className="space-y-2">
      <div className="eyebrow normal-case">{t("account.haveToken")}</div>
      <p className="text-sm text-muted">{t("account.restoreBlurb")}</p>
      <input className="input-x" placeholder={t("account.pastePlaceholder")} value={value} onChange={(e) => setValue(e.target.value)} />
      <button
        type="button"
        disabled={!value.trim()}
        onClick={() => { setUserToken(value.trim()); setValue(""); onClose(); }}
        className="btn w-full disabled:opacity-50"
      >
        {t("account.restore")}
      </button>
    </div>
  );
}
